import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import CancelSurveyModal from '../../components/CancelSurveyModal';

export default function Billing() {
  const { apiFetch, activeTeam } = useAuth();
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCancelSurvey, setShowCancelSurvey] = useState(false);

  const loadBilling = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/billing/subscription');
      setBillingData(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [apiFetch]);

  useEffect(() => { loadBilling(); }, [loadBilling]);

  const handleOpenPortal = async () => {
    setError(''); setSaving(true);
    try {
      const data = await apiFetch('/api/billing/portal', { method: 'POST' });
      window.location.href = data.portal_url;
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleCancel = async (cancelReason) => {
    setShowCancelSurvey(false);
    setError(''); setSaving(true);
    try {
      const data = await apiFetch('/api/billing/cancel', {
        method: 'POST',
        body: JSON.stringify({ cancel_reason: cancelReason }),
      });
      setSuccess(`Subscription canceled. Access continues until ${new Date(data.access_until).toLocaleDateString()}.`);
      loadBilling();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleReactivate = async () => {
    setError(''); setSaving(true);
    try {
      await apiFetch('/api/billing/reactivate', { method: 'POST' });
      setSuccess('Subscription reactivated.');
      loadBilling();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const sub = billingData?.subscription;

  if (activeTeam?.is_demo) {
    return (
      <div className="page-content">
        <h2 className="page-title" style={{ fontSize: '1.1rem' }}>Billing</h2>
        <div className="card">
          <p style={{ color: 'var(--text-muted)' }}>Billing is not available on demo accounts.</p>
        </div>
      </div>
    );
  }

  if (!sub) {
    return (
      <div className="page-content">
        <h2 className="page-title" style={{ fontSize: '1.1rem' }}>Billing</h2>
        <div className="card">
          <p style={{ color: 'var(--text-muted)' }}>No active subscription found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <h2 className="page-title" style={{ fontSize: '1.1rem' }}>Billing</h2>

      {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}
      {success && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 12 }}>{success}</div>}

      {/* Past due warning */}
      {sub.status === 'past_due' && (
        <div className="billing-past-due-banner">
          Payment failed. Please update your payment method to avoid service interruption.
          <button className="btn btn-sm btn-outline" onClick={handleOpenPortal} disabled={saving} style={{ marginLeft: 8 }}>
            Update Payment
          </button>
        </div>
      )}

      {/* Canceled notice */}
      {sub.canceled_at && (
        <div className="billing-canceled-notice">
          Your subscription will end on {new Date(sub.current_period_end).toLocaleDateString()}.
          <button className="btn btn-sm btn-outline" onClick={handleReactivate} disabled={saving} style={{ marginLeft: 8 }}>
            Reactivate
          </button>
        </div>
      )}

      {/* Plan info */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong style={{ fontSize: '1rem' }}>
              {sub.plan.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </strong>
            <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              ({sub.billing_interval})
            </span>
          </div>
          <div className="billing-status-badge" data-status={sub.status}>
            {sub.status === 'trialing' && sub.trial_end
              ? `Free Trial - Ends ${new Date(sub.trial_end).toLocaleDateString()}`
              : sub.status}
          </div>
        </div>
        {sub.per_player && (
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 8 }}>
            {sub.per_player_price} per player / {sub.billing_interval === 'annual' ? 'year' : 'month'}
          </div>
        )}
        {sub.current_period_end && sub.status !== 'trialing' && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 6 }}>
            Next billing date: {new Date(sub.current_period_end).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Roster / estimated bill */}
      {sub.per_player && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Active Roster</strong>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {sub.roster_count} player{sub.roster_count !== 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 6 }}>
            Estimated next bill: <strong style={{ color: 'var(--orange)' }}>{sub.estimated_next_bill}</strong>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
            Roster changes are synced daily. Add or remove players anytime.
          </div>
        </div>
      )}

      {/* Payment method */}
      {sub.card_last4 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <strong>Payment Method</strong>
          <div style={{ marginTop: 4, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            {sub.card_brand && sub.card_brand.charAt(0).toUpperCase() + sub.card_brand.slice(1)} ending in {sub.card_last4}
            <span style={{ marginLeft: 8 }}>
              (expires {sub.card_exp_month}/{sub.card_exp_year})
            </span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-outline btn-sm" onClick={handleOpenPortal} disabled={saving}>
          Manage Payment
        </button>
        {!sub.canceled_at && (
          <button className="btn btn-outline btn-sm" onClick={() => setShowCancelSurvey(true)} disabled={saving} style={{ color: '#e74c3c' }}>
            Cancel
          </button>
        )}
      </div>

      {showCancelSurvey && (
        <CancelSurveyModal
          onConfirm={handleCancel}
          onClose={() => setShowCancelSurvey(false)}
        />
      )}
    </div>
  );
}
